/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import SidebarEntryNavigationUIStateQuery, {
	type SidebarEntryNavigationUIStateQuery as SidebarEntryNavigationUIStateQueryType,
} from '@jira/navigation-apps__page-container-entries-sidebar-entry/src/__generated__/SidebarEntryNavigationUIStateQuery.graphql';
import type { PreloadedQuery } from 'react-relay';
import {
	MENU_ID_APPS,
	MENU_ID_FILTERS,
	MENU_ID_OPERATIONS,
	MENU_ID_PLANS,
	MENU_ID_PROJECTS,
} from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { getInitialSelectedPath } from '@jira/navigation-apps__sidebar-nav4-context/src/common/utils/selected-path/getInitialSelectedPath.tsx';
import { isSelectedPath } from '@jira/navigation-apps__sidebar-nav4-context/src/common/utils/selected-path/isSelectedPath.tsx';
import {
	createRelayResource,
	RELAY_RESOURCE_TYPE,
} from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { JiraNavigationSettingsAri } from '@atlassian/ari/jira/navigation-settings';
import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { fg } from '@atlassian/jira-feature-gating';
import SidebarNav4Query, {
	type L1MenuItemsQuery,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-content-core/src/ui/__generated__/L1MenuItemsQuery.graphql';
import type { RouteResource } from '@atlassian/react-resource-router';

const RESOURCE_TYPE_SIDEBAR_V2_RELAY = 'SIDEBAR_V2_RELAY';

export const sidebarNav4Resource: RouteResource<
	PreloadedQuery<L1MenuItemsQuery, Record<string, unknown>>
> = createRelayResource<L1MenuItemsQuery>({
	type: `${RELAY_RESOURCE_TYPE}_${RESOURCE_TYPE_SIDEBAR_V2_RELAY}`,
	getQuery: (
		routerContext,
		{ tenantContext: { cloudId, activationId, atlassianAccountId, isAnonymous } },
	) => {
		const initialSelectedPath = getInitialSelectedPath(routerContext);

		return {
			parameters: SidebarNav4Query,
			variables: {
				cloudId,
				isAppsExpanded: isSelectedPath(initialSelectedPath, MENU_ID_APPS),
				isFiltersExpanded: isSelectedPath(initialSelectedPath, MENU_ID_FILTERS),
				isPlansExpanded: isSelectedPath(initialSelectedPath, MENU_ID_PLANS),
				isProjectsExpanded: isSelectedPath(initialSelectedPath, MENU_ID_PROJECTS),
				isOperationsExpanded: isSelectedPath(initialSelectedPath, MENU_ID_OPERATIONS),
				entityAriFilters: JiraNavigationSettingsAri.create({
					siteId: cloudId,
					activationId,
					navigationSettingScope: 'filters',
				}).toString(),
				entityAriApps: JiraNavigationSettingsAri.create({
					siteId: cloudId,
					activationId,
					navigationSettingScope: 'apps',
				}).toString(),
				entityAriPlans: JiraNavigationSettingsAri.create({
					siteId: cloudId,
					activationId,
					navigationSettingScope: 'plans',
				}).toString(),
				ownerAri:
					atlassianAccountId && IdentityUserAri.create({ userId: atlassianAccountId }).toString(),
				isAnonymous,
				isRoadmapUpsellEnabled: fg('jpd-roadmaps-in-jira'),
			},
			options: { fetchPolicy: 'store-and-network' },
		};
	},
	isCritical: true,
});

export const sidebarEntryUiStateResource: RouteResource<
	PreloadedQuery<SidebarEntryNavigationUIStateQueryType, Record<string, unknown>>
> = createRelayResource<SidebarEntryNavigationUIStateQueryType>({
	type: `${RELAY_RESOURCE_TYPE}_SIDEBAR_ENTRY_UI_STATE_RELAY`,
	getQuery: (_, { tenantContext: { cloudId } }) => ({
		parameters: SidebarEntryNavigationUIStateQuery,
		variables: {
			cloudId,
			isSkipped: fg('navx-global-side-nav'),
		},
		options: { fetchPolicy: 'store-and-network' },
	}),
	isCritical: true,
});
